/**
 * Der Rahmen um alle Bildschirme: Navigation links, Inhalt rechts.
 *
 * Die Reihenfolge der Punkte folgt dem Bürotag, nicht der Technik: Was
 * morgens ansteht, steht oben. Stammdaten pflegt man nebenbei, Verwaltung
 * selten — also unten.
 *
 * Bereiche, die noch nicht gebaut sind, werden GEZEIGT und als „in Arbeit"
 * markiert, statt sie zu verstecken. Wer das Backoffice zum ersten Mal
 * öffnet, soll sehen, was kommt; und wer etwas sucht, das noch fehlt, soll
 * nicht suchen, sondern es lesen können.
 */

import { useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useSitzung } from './anmeldung/Sitzung';
import { Fehlergrenze } from './Fehlergrenze';

type Punkt = { pfad: string; text: string; fertig: boolean };
type Gruppe = { titel: string; punkte: Punkt[] };

const NAVIGATION: Gruppe[] = [
  {
    titel: 'Tagesgeschäft',
    punkte: [
      { pfad: '/disposition', text: 'Disposition', fertig: true },
      { pfad: '/offen', text: 'Offene Punkte', fertig: true },
    ],
  },
  {
    titel: 'Stammdaten',
    punkte: [
      { pfad: '/kunden', text: 'Kunden', fertig: true },
      { pfad: '/orte', text: 'Orte', fertig: true },
      { pfad: '/fahrzeuge', text: 'Fahrzeuge', fertig: true },
      { pfad: '/strecken', text: 'Streckenvorlagen', fertig: true },
    ],
  },
  {
    titel: 'Abrechnung',
    punkte: [
      { pfad: '/abrechnung', text: 'Aufträge abrechnen', fertig: true },
      { pfad: '/zeiten', text: 'Zeiten und Spesen', fertig: true },
    ],
  },
  {
    titel: 'Verwaltung',
    punkte: [
      { pfad: '/mitarbeiter', text: 'Mitarbeiter', fertig: true },
      { pfad: '/zugang', text: 'Mein Zugang', fertig: true },
    ],
  },
];

/** Der Zeitpunkt des Bauens, kurz und lesbar. */
const GEBAUT = new Date(__GEBAUT__).toLocaleString('de-DE', {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

export function Rahmen() {
  const { stand, abmelden } = useSitzung();
  const person = stand.art === 'bereit' ? stand.mitarbeiter : null;

  /* ═══ DAS MENÜ AUF SCHMALEN BILDSCHIRMEN ═══
   *
   * Auf breiten Bildschirmen steht die Navigation immer links; dieser Zustand
   * hat dort keine Wirkung, weil die Schublade erst unter 880 px entsteht
   * (siehe grundlagen.css). Es gibt bewusst KEINE Abfrage der Fensterbreite in
   * JavaScript: Zwei Stellen, die dieselbe Grenze kennen müssen, laufen
   * irgendwann auseinander — dann wäre das Menü zu und der Knopf unsichtbar.
   */
  const [menueOffen, setMenueOffen] = useState(false);

  // Escape schließt. Der Weg zurück muss ohne genaues Zielen möglich sein —
  // der Schleier daneben ist nicht immer getroffen.
  useEffect(() => {
    if (!menueOffen) return;
    const hoeren = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenueOffen(false);
    };
    window.addEventListener('keydown', hoeren);
    return () => window.removeEventListener('keydown', hoeren);
  }, [menueOffen]);

  return (
    <div className="rahmen">
      <div className="rahmen__leiste">
        <button
          type="button"
          className="rahmen__burger"
          aria-label={menueOffen ? 'Menü schließen' : 'Menü öffnen'}
          aria-expanded={menueOffen}
          onClick={() => setMenueOffen((offen) => !offen)}
        >
          <span />
          <span />
          <span />
        </button>
        <div className="rahmen__marke">
          <span>Haid &amp; Jerg</span>
          <strong>Backoffice</strong>
        </div>
      </div>

      {menueOffen && (
        <button
          type="button"
          className="rahmen__schleier"
          aria-label="Menü schließen"
          onClick={() => setMenueOffen(false)}
        />
      )}

      <nav
        className={`rahmen__navigation${menueOffen ? ' rahmen__navigation--offen' : ''}`}
      >
        <div className="rahmen__marke">
          <span>Haid &amp; Jerg</span>
          <strong>Backoffice</strong>
        </div>

        {NAVIGATION.map((gruppe) => (
          <div className="rahmen__gruppe" key={gruppe.titel}>
            <div className="rahmen__gruppentitel">{gruppe.titel}</div>
            {gruppe.punkte.map((punkt) => (
              <NavLink
                key={punkt.pfad}
                to={punkt.pfad}
                className={({ isActive }) =>
                  `rahmen__punkt${isActive ? ' rahmen__punkt--aktiv' : ''}`
                }
                /* Der Klick auf einen Punkt schließt die Schublade. Genau hier
                   und nicht als Wirkung auf den Adresswechsel: Der Klick IST
                   das Ereignis, das sie schließen soll. Am Adresswechsel zu
                   hängen hieße, dass auch der Zurück-Knopf des Browsers das
                   Menü zumacht — was niemand geöffnet hatte. */
                onClick={() => setMenueOffen(false)}
              >
                {punkt.text}
                {!punkt.fertig && (
                  <span
                    className="marker marker--neutral"
                    title="Dieser Bereich ist noch nicht gebaut"
                  >
                    bald
                  </span>
                )}
              </NavLink>
            ))}
          </div>
        ))}

        <div className="rahmen__fuss">
          <div className="rahmen__person">
            <span>{person?.rolle === 'admin' ? 'Admin' : 'Büro'}</span>
            {person ? `${person.vorname} ${person.nachname}` : '—'}
          </div>
          <button className="rahmen__punkt" onClick={() => void abmelden()}>
            Abmelden
          </button>
          {/* Der Stand der ausgelieferten Fassung. Sieht nach Kleinkram aus,
              beantwortet aber die Frage „läuft hier die neue Version" ohne
              Suchen — und die kam schon einmal teuer. */}
          <div className="rahmen__stand" title={`Gebaut am ${GEBAUT}`}>
            Stand {GEBAUT} · {__STAND__}
          </div>
        </div>
      </nav>

      <main className="rahmen__inhalt">
        {/* Die Grenze sitzt INNEN: Stürzt ein Bildschirm ab, bleibt die
            Navigation stehen und man kommt woanders hin, ohne neu zu laden. */}
        <Fehlergrenze>
          <Outlet />
        </Fehlergrenze>
      </main>
    </div>
  );
}

/** Kopfzeile eines Bildschirms — Titel, ein Satz Erklärung, Werkzeuge rechts. */
export function Seitenkopf(eigenschaften: {
  titel: string;
  /** Ein Satz — oder, wo ein Satz nicht reicht, eigenes Markup (Mitarbeiter). */
  erklaerung?: React.ReactNode;
  werkzeuge?: React.ReactNode;
}) {
  const { erklaerung } = eigenschaften;
  return (
    <header className="rahmen__kopf">
      <div>
        <h1>{eigenschaften.titel}</h1>
        {typeof erklaerung === 'string' ? <p>{erklaerung}</p> : erklaerung}
      </div>
      {eigenschaften.werkzeuge && (
        <div className="reihe">{eigenschaften.werkzeuge}</div>
      )}
    </header>
  );
}
